import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpIcon, MicIcon, PlusIcon, SparklesIcon } from 'lucide-react';
import { heroPrompts } from '../data/homepage';
import { AtnaConversationDialog } from './demandes/AtnaConversationDialog';
import { InscriptionDialog } from './loisirs/InscriptionDialog';
import { detectCategory } from '../hooks/useInscriptionConversation';
const PANEL_LAYOUT_ID = 'atna-hero-panel';
type Flow = 'signalement' | 'inscription';

/** Picks the most likely ATNA flow from what the resident typed. */
function detectFlow(text: string): Flow {
  const lower = text.toLowerCase();
  const wantsRegistration = /regist|sign\s?up|class|lesson|activit|camp|session|swim|skat|program|course/.test(lower) || detectCategory(lower) !== null;
  return wantsRegistration ? 'inscription' : 'signalement';
}
export function HeroAtna() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [conversation, setConversation] = useState<{
    flow: Flow;
    text: string;
  } | null>(null);
  const openConversation = (text: string, flow?: Flow) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setConversation({
      flow: flow ?? detectFlow(trimmed),
      text: trimmed
    });
    setValue('');
  };
  return <section aria-labelledby="hero-atna-title" className="w-full rounded-3xl border border-white/60 bg-white p-5 shadow-2xl shadow-ville-navy/20 sm:p-6" data-id="element-1295" data-mp="omv5nhv3ux87">
      <h2 id="hero-atna-title" className="sr-only" data-id="element-1296" data-mp="1hck6nv7tex9t">
        ATNA assistant
      </h2>

      {conversation ?
    // Holds the height while the bar morphs into the dialog.
    <div className="h-[54px]" aria-hidden="true" data-id="element-1297" data-mp="18coq1avhqa2e" /> : <motion.div layoutId={PANEL_LAYOUT_ID} style={{
      borderRadius: 999
    }} transition={{
      duration: 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-1298" data-mp="18bdmnc5yj4bt">
          <form onSubmit={event => {
        event.preventDefault();
        openConversation(value);
      }} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1.5 pl-3 pr-1.5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-1299" data-mp="3t3p8nnpn33m7">
            <button type="button" onClick={() => openConversation(value)} aria-label="Attach a photo or a document" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1300" data-mp="1oww7pylgx2fu">
              <PlusIcon className="h-5 w-5" aria-hidden="true" data-id="element-1301" data-mp="1aw1v13re0fbd" />
            </button>
            <label htmlFor="hero-atna-input" className="sr-only" data-id="element-1302" data-mp="15z1pctyg9eks">
              Ask ATNA
            </label>
            <input id="hero-atna-input" type="text" value={value} onChange={event => setValue(event.target.value)} placeholder="Ask ATNA…" className="min-w-0 flex-1 border-0 bg-transparent py-2 text-base text-ville-navy placeholder:text-slate-400 focus:outline-none" data-id="element-1303" data-mp="11y9fbyhr75ng" />
            <button type="button" aria-label="Dictate your question" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1304" data-mp="i3q8tbkmkmoh">
              <MicIcon className="h-5 w-5" aria-hidden="true" data-id="element-1305" data-mp="vta7o6b34zsi" />
            </button>
            <button type="submit" aria-label="Send your question to ATNA" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ville-lime text-ville-navy transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1306" data-mp="3va8h76ujsx57">
              <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-1307" data-mp="d557t9xjtyyz" />
            </button>
          </form>
        </motion.div>}

      <ul className="mt-3 flex flex-wrap gap-2" data-id="element-1308" data-mp="1jmab67jolyal">
        {heroPrompts.map(prompt => <li key={prompt.label} data-id="element-1309" data-mp="1oh62t6l0ajn6">
            <button type="button" onClick={() => prompt.label === 'Register my child' ? openConversation(prompt.label, 'inscription') : navigate(prompt.to)} className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1310" data-mp="1n1lh5dvty00s">
              {prompt.label}
            </button>
          </li>)}
      </ul>

      <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-ville-indigo" data-id="element-1311" data-mp="210l8itaq8t0n">
        <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1312" data-mp="2fphbwd1u9hux" />
        ATNA knows the Town of Bonnyville’s services
      </p>

      <AnimatePresence data-id="element-1313" data-mp="2as7pz7dnrobu">
        {conversation?.flow === 'signalement' && <AtnaConversationDialog key="atna-hero-dialog" initialText={conversation.text} layoutId={PANEL_LAYOUT_ID} onClose={() => setConversation(null)} data-id="element-1314" data-mp="2oqff4t4gdlcg" />}
        {conversation?.flow === 'inscription' && <InscriptionDialog key="atna-hero-inscription" initialText={conversation.text} layoutId={PANEL_LAYOUT_ID} onClose={() => setConversation(null)} data-id="element-1315" data-mp="33dmm6nqthz62" />}
      </AnimatePresence>
    </section>;
}